import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Heading1, Heading2, Heading3, Italic, List, Underline as UnderlineIcon } from "lucide-react";
import { useEffect, useRef } from "react";
import styles from "./RichTextEditor.module.css";

export { styles };

export type TiptapEditor = NonNullable<ReturnType<typeof useEditor>>;

interface ToolbarButtonProps {
  onClick: () => void;
  isActive: boolean;
  ariaLabel: string;
  children: React.ReactNode;
}

function ToolbarButton({ onClick, isActive, ariaLabel, children }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        onClick();
      }}
      aria-label={ariaLabel}
      className={`p-1.5 rounded-4 transition-colors ${
        isActive ? "bg-gray-200 text-gray-1000" : "text-gray-600 hover:bg-gray-100 hover:text-gray-1000"
      }`}
    >
      {children}
    </button>
  );
}

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  onBlur?: () => void;
  onEditorReady?: (editor: TiptapEditor) => void;
  placeholder?: string;
  disabled?: boolean;
}

export function RichTextEditor({ value, onChange, onBlur, onEditorReady, placeholder, disabled }: RichTextEditorProps) {
  const onEditorReadyRef = useRef(onEditorReady);
  onEditorReadyRef.current = onEditorReady;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: e }) => {
      onChange(e.getHTML());
    },
    onBlur: () => {
      onBlur?.();
    },
    onCreate: ({ editor: e }) => {
      onEditorReadyRef.current?.(e);
    },
    immediatelyRender: false,
  });

  // Sync external value changes (e.g. AI Refine) into the editor
  const prevValueRef = useRef(value);
  useEffect(() => {
    if (!editor) return;
    if (value === prevValueRef.current) return;
    prevValueRef.current = value;

    const currentHTML = editor.getHTML();
    if (value !== currentHTML) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  const isEmpty = !editor?.getText().trim();

  if (!editor) return null;

  return (
    <div className="flex flex-col rounded-md border border-gray-200 bg-white overflow-hidden focus-within:border-gray-400 focus-within:ring-1 focus-within:ring-gray-400">
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-gray-200 bg-gray-50">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          isActive={editor.isActive("heading", { level: 1 })}
          ariaLabel="Heading 1"
        >
          <Heading1 width={18} height={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          isActive={editor.isActive("heading", { level: 2 })}
          ariaLabel="Heading 2"
        >
          <Heading2 width={18} height={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          isActive={editor.isActive("heading", { level: 3 })}
          ariaLabel="Heading 3"
        >
          <Heading3 width={18} height={18} />
        </ToolbarButton>
        <div className="w-px h-4 bg-gray-200 mx-1" />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          isActive={editor.isActive("bold")}
          ariaLabel="Bold"
        >
          <Bold width={18} height={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          isActive={editor.isActive("italic")}
          ariaLabel="Italic"
        >
          <Italic width={18} height={18} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          isActive={editor.isActive("underline")}
          ariaLabel="Underline"
        >
          <UnderlineIcon width={18} height={18} />
        </ToolbarButton>
        <div className="w-px h-4 bg-gray-200 mx-1" />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          isActive={editor.isActive("bulletList")}
          ariaLabel="Bullet list"
        >
          <List width={18} height={18} />
        </ToolbarButton>
      </div>

      <div className={`relative p-3 ${styles.richTextContent}`}>
        {isEmpty && placeholder && (
          <p className="absolute top-3 left-3 text-gray-400 pointer-events-none select-none text-body4">
            {placeholder}
          </p>
        )}
        <EditorContent editor={editor} className={styles.editorContent} />
      </div>
    </div>
  );
}
